/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * V2 Supabase client (fetch-based PostgREST wrapper).
 *
 * V1 frontend reads showcase data (case studies, modules, resources,
 * simulations) from V2 Supabase project (hbkuszknwmhipwyzjwzc) so
 * that when V2 frontend ships there is zero data migration.
 *
 * Implementation note: this is a thin fetch wrapper around Supabase's
 * PostgREST endpoint rather than the official @supabase/supabase-js
 * SDK. The SDK is preferred long-term but cannot be installed in the
 * current local environment due to a TLS cert chain issue with the
 * registry. The call surface mirrors the SDK's basic chain so we can
 * migrate without changing consumers:
 *
 *   const { data, error } = await v2Supabase
 *     .from('case_studies')
 *     .select('*')
 *     .eq('domain', 'wms')
 *     .order('sequence_number')
 *     .execute();
 *
 * Anon key is read-only against tables protected by RLS:
 *   - case_studies, showcase_modules, resources, showcase_simulations
 * are SELECT-only for the anon role. Writes require the service role
 * (kept server-side, never bundled into the React build).
 */

const url = process.env.REACT_APP_V2_SUPABASE_URL;
const anonKey = process.env.REACT_APP_V2_SUPABASE_ANON_KEY;

if (!url || !anonKey || anonKey === 'REACT_APP_V2_SUPABASE_ANON_KEY_PLACEHOLDER') {
  // Don't throw — let pages render an empty state and surface the
  // misconfiguration in the console.
  // eslint-disable-next-line no-console
  console.warn(
    '[v2Supabase] REACT_APP_V2_SUPABASE_URL / REACT_APP_V2_SUPABASE_ANON_KEY ' +
    'are missing or placeholder. Showcase pages will render empty until env ' +
    'vars are set in Vercel.'
  );
}

const REST_BASE = `${url || ''}/rest/v1`;

const baseHeaders = () => ({
  apikey: anonKey || '',
  Authorization: `Bearer ${anonKey || ''}`,
  'Content-Type': 'application/json',
  Accept: 'application/json',
});

class QueryBuilder {
  constructor(table) {
    this.table = table;
    this.params = new URLSearchParams();
    this.headers = baseHeaders();
    this.single = false;
  }

  select(columns = '*') {
    this.params.set('select', columns);
    return this;
  }

  eq(column, value) {
    this.params.append(column, `eq.${value}`);
    return this;
  }

  in(column, values) {
    this.params.append(column, `in.(${values.map(encodeURIComponent).join(',')})`);
    return this;
  }

  order(column, { ascending = true } = {}) {
    const dir = ascending ? 'asc' : 'desc';
    const existing = this.params.get('order');
    const next = existing ? `${existing},${column}.${dir}` : `${column}.${dir}`;
    this.params.set('order', next);
    return this;
  }

  limit(n) {
    this.params.set('limit', String(n));
    return this;
  }

  maybeSingle() {
    // PostgREST's vnd.pgrst.object+json Accept variant returns 406 for
    // zero-row results which is awkward to interpret. We instead request
    // an array as normal and unwrap to first row in execute() — same
    // contract as @supabase/supabase-js .maybeSingle().
    this.single = true;
    return this;
  }

  async execute() {
    const endpoint = `${REST_BASE}/${this.table}?${this.params.toString()}`;
    try {
      const res = await fetch(endpoint, { headers: this.headers });
      if (!res.ok) {
        const text = await res.text();
        return { data: null, error: { status: res.status, message: text } };
      }
      const data = await res.json();
      if (this.single) {
        const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
        return { data: row, error: null };
      }
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message } };
    }
  }

  // Convenience: await the builder directly to run the query.
  then(resolve, reject) {
    return this.execute().then(resolve, reject);
  }
}

export const v2Supabase = {
  from: (table) => new QueryBuilder(table),
};

export default v2Supabase;
